import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, auth } from './firebase';

const CATEGORIES = ['Western Wear', 'Ethnic Wear', 'Formal Wear', 'Co-ord Sets'];

const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];

export default function AdminApp() {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState('bookings');

  // Bookings state
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // Products state
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Product form state
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productSubCategory, setProductSubCategory] = useState('');
  const [productSizes, setProductSizes] = useState([]);
  const [sizeChart, setSizeChart] = useState({});
  const [stock, setStock] = useState({});
  const [productPrice, setProductPrice] = useState('');
  const [productMrp, setProductMrp] = useState('');
  const [productImage, setProductImage] = useState('');
  const [productInStock, setProductInStock] = useState(true);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productFormError, setProductFormError] = useState('');
  const [productFormSuccess, setProductFormSuccess] = useState('');

  const loadBookings = async () => {
    setLoadingBookings(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'bookings'));
      const bookingsData = [];
      querySnapshot.forEach((doc) => {
        bookingsData.push({ id: doc.id, ...doc.data() });
      });
      // Sort newest first by createdAt
      bookingsData.sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bTime - aTime;
      });
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
    setLoadingBookings(false);
  };

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      const productsData = [];
      querySnapshot.forEach((doc) => {
        productsData.push({ id: doc.id, ...doc.data() });
      });
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    }
    setLoadingProducts(false);
  };

  useEffect(() => {
    if (isLoggedIn) {
      loadBookings();
      loadProducts();
    }
  }, [isLoggedIn]);

  const handleLogin = async () => {
    setLoginError('');
    if (!email || !password) {
      setLoginError('Please enter both email and password.');
      return;
    }
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setIsLoggedIn(true);
      setEmail('');
      setPassword('');
    } catch (error) {
      setLoginError('Wrong email or password. Please try again.');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setIsLoggedIn(false);
    setBookings([]);
    setProducts([]);
  };

  const toggleSize = (size) => {
    if (productSizes.includes(size)) {
      setProductSizes(productSizes.filter((s) => s !== size));
      const newChart = { ...sizeChart };
      delete newChart[size];
      setSizeChart(newChart);
      const newStock = { ...stock };
      delete newStock[size];
      setStock(newStock);
    } else {
      setProductSizes([...productSizes, size]);
    }
  };

  const resetProductForm = () => {
    setProductName('');
    setProductDescription('');
    setProductCategory('');
    setProductSubCategory('');
    setProductSizes([]);
    setSizeChart({});
    setStock({});
    setProductPrice('');
setProductMrp('');
setProductImage('');
    setProductInStock(true);
    setProductFormError('');
    setProductFormSuccess('');
    setEditingProductId(null);
  };

  const handleEditProduct = (product) => {
    setEditingProductId(product.id);
    setProductName(product.name || '');
    setProductDescription(product.description || '');
    setProductCategory(product.category || '');
    setProductSubCategory(product.subCategory || '');
    setProductSizes(Array.isArray(product.sizes) ? product.sizes : []);
    setSizeChart(product.sizeChart || {});
    setStock(product.stock || {});
    const priceNumber = product.price ? String(product.price).replace(/[₹,]/g, '').trim() : '';
    setProductPrice(priceNumber);
    const mrpNumber = product.mrp ? String(product.mrp).replace(/[₹,]/g, '').trim() : '';
setProductMrp(mrpNumber);
    setProductImage(product.image || '');
    setProductInStock(product.inStock !== false);
    setProductFormError('');
    setProductFormSuccess('');
    setShowProductForm(true);
  };
  const STATUS_OPTIONS = [
    { key: 'booked',           label: 'BOOKED',           color: '#888888' },
    { key: 'out_for_delivery', label: 'OUT FOR DELIVERY', color: '#1d4ed8' },
    { key: 'in_trial',         label: 'IN TRIAL',         color: '#9c7d4f' },
    { key: 'paid',             label: 'PAID ✓',           color: '#16a34a' },
    { key: 'completed',        label: 'COMPLETED',        color: '#1a1a1a' },
  ];

  const deductStockForBooking = async (bookingDocId) => {
    const booking = bookings.find((b) => b.id === bookingDocId);
    if (!booking || booking.stockDeducted) return;
    const items = Array.isArray(booking.purchasedItems) ? booking.purchasedItems : [];
    if (items.length === 0) return;
    try {
      for (const item of items) {
        if (!item.size || item.size === 'Not specified') continue;
        const product = products.find((p) => p.name === item.name);
        if (!product) continue;
        const currentStock = product.stock || {};
        const newQty = Math.max(0, (parseInt(currentStock[item.size], 10) || 0) - 1);
        await updateDoc(doc(db, 'products', product.id), {
          stock: { ...currentStock, [item.size]: newQty },
        });
      }
      await updateDoc(doc(db, 'bookings', bookingDocId), { stockDeducted: true });
      loadProducts();
    } catch (error) {
      console.error('Error deducting stock:', error);
    }
  };

  const updateBookingStatus = async (bookingDocId, newStatus) => {
    try {
      await updateDoc(doc(db, 'bookings', bookingDocId), { orderStatus: newStatus });
      if (newStatus === 'paid') {
        await deductStockForBooking(bookingDocId);
      }
      loadBookings();
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert('Could not update status. Please try again.');
    }
  };
  const handleSaveProduct = async () => {
    setProductFormError('');
    setProductFormSuccess('');

    if (!productName.trim()) {
      setProductFormError('Please enter a product name.');
      return;
    }
    if (!productCategory) {
      setProductFormError('Please select a category.');
      return;
    }
    if (!productPrice || isNaN(productPrice) || parseFloat(productPrice) <= 0) {
      setProductFormError('Please enter a valid price (numbers only).');
      return;
    }
    if (productSizes.length === 0) {
      setProductFormError('Please select at least one available size.');
      return;
    }
    if (!productImage.trim()) {
      setProductFormError('Please enter an image URL.');
      return;
    }
if (productMrp && parseFloat(productMrp) <= parseFloat(productPrice)) {
      setProductFormError('MRP must be higher than selling price (or leave MRP blank if no discount).');
      return;
    }
    setSavingProduct(true);
    try {
      const productData = {
        name: productName.trim(),
        description: productDescription.trim(),
        category: productCategory,
        subCategory: productSubCategory || '',
        sizes: productSizes,
        price: `₹${parseFloat(productPrice).toLocaleString('en-IN')}`,
        mrp: productMrp ? `₹${parseFloat(productMrp).toLocaleString('en-IN')}` : '',
        image: productImage.trim(),
        inStock: productInStock,
        sizeChart: sizeChart,
        stock: Object.fromEntries(
          Object.entries(stock).map(([k, v]) => [k, parseInt(v, 10) || 0])
        ),
      };

      if (editingProductId) {
        await updateDoc(doc(db, 'products', editingProductId), productData);
        setProductFormSuccess('Product updated successfully!');
      } else {
        productData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'products'), productData);
        setProductFormSuccess('Product added successfully!');
      }

      loadProducts();
      setTimeout(() => {
        setShowProductForm(false);
        resetProductForm();
      }, 1500);
    } catch (error) {
      console.error('Error saving product:', error);
      setProductFormError('Could not save product. Please try again.');
    }
    setSavingProduct(false);
  };

  const inputStyle = {
    width: '100%',
    padding: '14px',
    marginBottom: '18px',
    borderRadius: '12px',
    border: '1px solid #ddd',
    fontSize: '16px',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#111',
    marginBottom: '8px',
  };

  // LOGIN SCREEN
  if (!isLoggedIn) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#f5f5f5',
        padding: '20px',
        fontFamily: 'Arial',
      }}>
        <div style={{
          width: '100%',
          maxWidth: '420px',
          background: 'white',
          borderRadius: '24px',
          padding: '40px 35px',
        }}>
          <h1 style={{
            fontFamily: 'Georgia, serif',
            fontSize: '32px',
            color: '#111',
            margin: 0,
            marginBottom: '8px',
            textAlign: 'center',
            letterSpacing: '5px',
          }}>FIVETRY</h1>
          <p style={{
            fontSize: '12px',
            color: '#888',
            textAlign: 'center',
            marginBottom: '32px',
            letterSpacing: '2px',
          }}>ADMIN LOGIN</p>

          <input
            type="email"
            placeholder="Admin Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
            style={inputStyle}
          />

          {loginError && (
            <p style={{
              color: '#dc2626',
              fontSize: '14px',
              fontWeight: '600',
              marginTop: '-8px',
              marginBottom: '18px',
            }}>{loginError}</p>
          )}

          <button onClick={handleLogin} style={{
            width: '100%',
            background: '#111',
            color: 'white',
            border: 'none',
            padding: '16px',
            borderRadius: '14px',
            fontSize: '17px',
            fontWeight: '600',
            cursor: 'pointer',
            marginTop: '10px',
          }}>Login</button>
        </div>
      </div>
    );
  }

  // ADMIN PANEL (logged in)
  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f5',
      fontFamily: 'Arial',
    }}>
      {/* TOP BAR */}
      <div style={{
        background: '#111',
        color: 'white',
        padding: '20px 30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <h1 style={{
          fontFamily: 'Georgia, serif',
          fontSize: '22px',
          margin: 0,
          letterSpacing: '5px',
        }}>FIVETRY ADMIN</h1>

        <button onClick={handleLogout} style={{
          border: 'none',
          background: '#16a34a',
          color: 'white',
          padding: '10px 18px',
          borderRadius: '10px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
        }}>Logout</button>
      </div>

      {/* TABS */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e5e5',
        padding: '0 30px',
        display: 'flex',
        gap: '32px',
      }}>
        <button onClick={() => setActiveTab('bookings')} style={{
          background: 'none',
          border: 'none',
          padding: '18px 0',
          fontSize: '15px',
          fontWeight: '600',
          cursor: 'pointer',
          color: activeTab === 'bookings' ? '#111' : '#888',
          borderBottom: activeTab === 'bookings' ? '2px solid #111' : '2px solid transparent',
        }}>Bookings ({bookings.length})</button>

        <button onClick={() => setActiveTab('products')} style={{
          background: 'none',
          border: 'none',
          padding: '18px 0',
          fontSize: '15px',
          fontWeight: '600',
          cursor: 'pointer',
          color: activeTab === 'products' ? '#111' : '#888',
          borderBottom: activeTab === 'products' ? '2px solid #111' : '2px solid transparent',
        }}>Products ({products.length})</button>
      </div>

      {/* MAIN CONTENT */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '30px 20px',
      }}>

        {/* BOOKINGS TAB */}
        {activeTab === 'bookings' && (
          <>
            <h2 style={{ color: '#111', marginBottom: '24px' }}>Bookings</h2>

            {loadingBookings ? (
              <p style={{ color: '#666' }}>Loading bookings...</p>
            ) : bookings.length === 0 ? (
              <p style={{ color: '#666' }}>No bookings yet.</p>
            ) : (
              bookings.map((booking, index) => (
                <div key={index} style={{
                  background: 'white',
                  borderRadius: '18px',
                  padding: '20px',
                  marginBottom: '20px',
                }}>
                  <h3 style={{
                    marginTop: 0,
                    color: '#16a34a',
                    marginBottom: '12px',
                  }}>{booking.bookingId}</h3>

                  <button
                    onClick={() => {
                      const link = window.location.origin + '/deliver/' + booking.bookingId;
                      navigator.clipboard.writeText(link);
                      alert('Delivery link copied!\n\n' + link + '\n\nSend it to your courier on WhatsApp. When they open it and tap Mark Delivered, the trial starts.');
                    }}
                    style={{
                      background: 'white', color: '#1d4ed8', border: '1px solid #1d4ed8',
                      padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700',
                      cursor: 'pointer', marginBottom: '14px', letterSpacing: '0.5px',
                    }}
                  >📋 Copy delivery link</button>
                   {/* ORDER STATUS SECTION */}
                  <div style={{
                    background: '#fafaf7',
                    border: '1px solid #eeeae3',
                    borderRadius: '12px',
                    padding: '14px',
                    marginBottom: '16px',
                  }}>
                    {(() => {
                      const currentKey = booking.orderStatus || 'booked';
                      const current = STATUS_OPTIONS.find(o => o.key === currentKey) || STATUS_OPTIONS[0];
                      return (
                        <>
                          <div style={{ marginBottom: '12px' }}>
                            <span style={{
                              display: 'inline-block',
                              background: current.color,
                              color: 'white',
                              padding: '6px 14px',
                              borderRadius: '999px',
                              fontSize: '11px',
                              fontWeight: '700',
                              letterSpacing: '1.5px',
                            }}>{current.label}</span>
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: '#888',
                            letterSpacing: '1.5px',
                            fontWeight: '600',
                            marginBottom: '8px',
                          }}>UPDATE STATUS</div>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {STATUS_OPTIONS.map((option) => {
                              const isActive = currentKey === option.key;
                              return (
                                <button
                                  key={option.key}
                                  onClick={() => updateBookingStatus(booking.id, option.key)}
                                  disabled={isActive}
                                  style={{
                                    background: isActive ? option.color : 'white',
                                    color: isActive ? 'white' : option.color,
                                    border: '1px solid ' + option.color,
                                    padding: '6px 10px',
                                    borderRadius: '999px',
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    cursor: isActive ? 'default' : 'pointer',
                                    letterSpacing: '0.5px',
                                    opacity: isActive ? 0.7 : 1,
                                  }}
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <p style={{ margin: '6px 0' }}><strong>Name:</strong> {booking.customerName}</p>
                  <p style={{ margin: '6px 0' }}><strong>Phone:</strong> {booking.customerPhone}</p>
                  <p style={{ margin: '6px 0' }}><strong>Address:</strong> {booking.customerAddress}</p>
                  <p style={{ margin: '6px 0' }}><strong>Delivery:</strong> {booking.deliveryDay}</p>
                  <p style={{ margin: '6px 0' }}><strong>Time Slot:</strong> {booking.timeSlot}</p>
                  <p style={{ margin: '6px 0' }}><strong>Items:</strong> {booking.itemsSelected}</p>

                  {booking.selectedItems && Array.isArray(booking.selectedItems) && booking.selectedItems.length > 0 && (
                    <div style={{ marginTop: '14px' }}>
                      <strong>Selected Items:</strong>
                      <div style={{
                        marginTop: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                      }}>
                        {booking.selectedItems.map((item, idx) => {
                          if (typeof item === 'string') {
                            return (
                              <div key={idx} style={{
                                padding: '10px 14px',
                                background: '#f5f5f5',
                                borderRadius: '10px',
                                fontSize: '14px',
                                color: '#666',
                              }}>
                                {item}
                                <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '8px', fontStyle: 'italic' }}>
                                  (older booking — no image saved)
                                </span>
                              </div>
                            );
                          }
                          return (
                            <div key={idx} style={{
                              display: 'flex',
                              gap: '14px',
                              alignItems: 'center',
                              padding: '12px',
                              background: '#f5f5f5',
                              borderRadius: '12px',
                            }}>
                              {item.image && (
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  style={{
                                    width: '70px',
                                    height: '90px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    background: '#ddd',
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              <div style={{ flex: 1 }}>
                                <p style={{ margin: '0 0 4px', fontWeight: '600', color: '#111', fontSize: '15px' }}>
                                  {item.name}
                                </p>
                                {item.price && (
                                  <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                                    {item.price}
                                  </p>
                                )}
                                {item.size && item.size !== 'Not specified' && (
                                  <div style={{
                                    marginTop: '8px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                  }}>
                                    <span style={{
                                      fontSize: '11px',
                                      color: '#888',
                                      fontWeight: '600',
                                      letterSpacing: '1px',
                                      textTransform: 'uppercase',
                                    }}>Size</span>
                                    <span style={{
                                      background: '#9c7d4f',
                                      color: 'white',
                                      padding: '3px 12px',
                                      borderRadius: '999px',
                                      fontSize: '13px',
                                      fontWeight: '700',
                                      letterSpacing: '0.5px',
                                    }}>{item.size}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </>
        )}

        {/* PRODUCTS TAB */}
        {activeTab === 'products' && (
          <>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}>
              <h2 style={{ color: '#111', margin: 0 }}>Products</h2>

              <button onClick={() => {
                resetProductForm();
                setShowProductForm(true);
              }} style={{
                background: '#111',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}>+ Add New Product</button>
            </div>

            {loadingProducts ? (
              <p style={{ color: '#666' }}>Loading products...</p>
            ) : products.length === 0 ? (
              <p style={{ color: '#666' }}>No products yet. Click "Add New Product" to get started.</p>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '16px',
              }}>
                {products.map((product, index) => (
                  <div key={index} style={{
                    background: 'white',
                    borderRadius: '14px',
                    padding: '14px',
                    overflow: 'hidden',
                  }}>
                    {product.image && (
                      <img
                        src={product.image}
                        alt={product.name}
                        style={{
                          width: '100%',
                          height: '220px',
                          objectFit: 'cover',
                          borderRadius: '10px',
                          background: '#f0f0f0',
                          marginBottom: '12px',
                        }}
                      />
                    )}
                    <p style={{
                      margin: '0 0 4px',
                      fontSize: '11px',
                      color: '#888',
                      letterSpacing: '1px',
                    }}>{product.category?.toUpperCase()}</p>
                    <p style={{
                      margin: '0 0 6px',
                      fontWeight: '600',
                      color: '#111',
                      fontSize: '14px',
                    }}>{product.name}</p>
                    
                    <div style={{
  margin: '0 0 6px',
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  flexWrap: 'wrap',
}}>
  <span style={{ color: '#111', fontWeight: '600', fontSize: '14px' }}>
    {product.price}
  </span>
  {product.mrp && (() => {
    const priceNum = parseFloat(String(product.price).replace(/[₹,]/g, ''));
    const mrpNum = parseFloat(String(product.mrp).replace(/[₹,]/g, ''));
    if (mrpNum > priceNum) {
      const discount = Math.round(((mrpNum - priceNum) / mrpNum) * 100);
      return (
        <>
          <span style={{
            textDecoration: 'line-through',
            color: '#999',
            fontSize: '12px',
          }}>{product.mrp}</span>
          <span style={{
            color: '#16a34a',
            fontSize: '11px',
            fontWeight: '600',
          }}>{discount}% OFF</span>
        </>
      );
    }
    return null;
  })()}
</div>
                    {product.inStock === false && (
                      <p style={{
                        margin: '6px 0 0',
                        fontSize: '11px',
                        color: '#dc2626',
                        fontWeight: '600',
                      }}>OUT OF STOCK</p>
                    )}

                    {/* EDIT + DELETE BUTTONS */}
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      marginTop: '12px',
                    }}>
                      <button onClick={() => handleEditProduct(product)} style={{
                        flex: 1,
                        background: '#111',
                        color: 'white',
                        border: 'none',
                        padding: '8px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}>Edit</button>
                      <button onClick={() => handleDeleteProduct(product.id, product.name)} style={{
                        flex: 1,
                        background: 'white',
                        color: '#dc2626',
                        border: '1px solid #dc2626',
                        padding: '8px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

      </div>

      {/* PRODUCT FORM MODAL (Add OR Edit) */}
      {showProductForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 5500,
          padding: '20px',
          boxSizing: 'border-box',
          overflowY: 'auto',
        }}>
          <div style={{
            width: '100%',
            maxWidth: '500px',
            background: 'white',
            borderRadius: '24px',
            padding: '32px',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}>
              <h2 style={{ margin: 0, color: '#111' }}>
                {editingProductId ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button onClick={() => {
                setShowProductForm(false);
                resetProductForm();
              }} style={{
                border: 'none',
                background: 'transparent',
                fontSize: '24px',
                cursor: 'pointer',
              }}>×</button>
            </div>

            <label style={labelStyle}>Product Name</label>
            <input
              type="text"
              placeholder="e.g., Teal Blue cotton rayon 2 piece set"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              style={inputStyle}
            />

            <label style={labelStyle}>Description (fabric, fit, design — helps customers decide)</label>
            <textarea
              placeholder="e.g., Soft cotton-rayon blend, relaxed fit with side pockets. Pairs easily with flats or heels."
              value={productDescription}
              onChange={(e) => setProductDescription(e.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '90px', fontFamily: 'Arial' }}
            />

            <label style={labelStyle}>Category</label>
            <select
              value={productCategory}
              onChange={(e) => setProductCategory(e.target.value)}
              style={{ ...inputStyle, background: 'white' }}
            >
              <option value="">Select category</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <label style={labelStyle}>Available Sizes (tap to select all that apply)</label>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '18px',
            }}>
              {SIZES.map((size) => {
                const selected = productSizes.includes(size);
                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() => toggleSize(size)}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '10px',
                      border: selected ? '2px solid #111' : '1px solid #ddd',
                      background: selected ? '#111' : 'white',
                      color: selected ? 'white' : '#111',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >{size}</button>
                );
              })}
            </div>
            {productSizes.length > 0 && (
              <>
                <label style={labelStyle}>Size Chart — Chest in inches (optional but recommended)</label>
                <div style={{
                  background: '#fafaf7',
                  border: '1px solid #eeeae3',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '8px',
                }}>
                  {SIZES.filter(s => productSizes.includes(s)).map((size) => (
                    <div key={size} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '10px',
                    }}>
                      <span style={{
                        minWidth: '50px',
                        fontWeight: '700',
                        color: '#111',
                        fontSize: '14px',
                      }}>{size}</span>
                      <input
                        type="number"
                        placeholder="e.g., 36"
                        value={sizeChart[size] || ''}
                        onChange={(e) => setSizeChart({
                          ...sizeChart,
                          [size]: e.target.value,
                        })}
                        style={{
                          flex: 1,
                          padding: '10px 14px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                      <span style={{
                        color: '#888',
                        fontSize: '13px',
                        minWidth: '50px',
                      }}>inches</span>
                    </div>
                  ))}
                </div>
                <p style={{
                  fontSize: '12px',
                  color: '#888',
                  marginTop: '0',
                  marginBottom: '18px',
                }}>Customers will see chest measurements next to each size. Leave blank to skip.</p>
              </>
            )}
            {productSizes.length > 0 && (
              <>
                <label style={labelStyle}>Stock — quantity per size</label>
                <div style={{
                  background: '#fafaf7',
                  border: '1px solid #eeeae3',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '8px',
                }}>
                  {SIZES.filter(s => productSizes.includes(s)).map((size) => (
                    <div key={size} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '10px',
                    }}>
                      <span style={{
                        minWidth: '50px',
                        fontWeight: '700',
                        color: '#111',
                        fontSize: '14px',
                      }}>{size}</span>
                      <input
                        type="number"
                        min="0"
                        placeholder="e.g., 5"
                        value={stock[size] ?? ''}
                        onChange={(e) => setStock({
                          ...stock,
                          [size]: e.target.value,
                        })}
                        style={{
                          flex: 1,
                          padding: '10px 14px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                      <span style={{
                        color: '#888',
                        fontSize: '13px',
                        minWidth: '50px',
                      }}>pcs</span>
                    </div>
                  ))}
                </div>
                <p style={{
                  fontSize: '12px',
                  color: '#888',
                  marginTop: '0',
                  marginBottom: '18px',
                }}>How many pieces you have of each size. Sizes at 0 auto-show as sold out to customers.</p>
              </>
            )}
            <label style={labelStyle}>MRP / Original Price in ₹ (optional)</label>
            <input
              type="number"
              placeholder="e.g., 2999"
              value={productMrp}
              onChange={(e) => setProductMrp(e.target.value)}
              style={inputStyle}
            />
            <p style={{
              fontSize: '12px',
              color: '#888',
              marginTop: '-12px',
              marginBottom: '18px',
            }}>Leave blank if no discount. Discount % auto-calculates.</p>

            <label style={labelStyle}>Selling Price in ₹ (what customer actually pays)</label>
            <input
              type="number"
              placeholder="e.g., 1799"
              value={productPrice}
              onChange={(e) => setProductPrice(e.target.value)}
              style={inputStyle}
            />
            <p style={{
              fontSize: '12px',
              color: '#888',
              marginTop: '-12px',
              marginBottom: '18px',
            }}>Upload your photo to imgbb.com, copy the "Direct link" URL, paste here.</p>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#111',
              marginBottom: '24px',
              cursor: 'pointer',
            }}>
              <input
               type="checkbox"
               checked={productInStock}
               onChange={(e) => setProductInStock(e.target.checked)}
               style={{ width: '18px', height: '18px' }}
              />
              In stock (visible to customers)
            </label>

              <label style={labelStyle}>Product Image URL</label>
              <input
               type="text"
               placeholder="https://i.ibb.co/.../image.jpg"
               value={productImage}
               onChange={(e) => setProductImage(e.target.value)}
               style={inputStyle}
            />
            {productFormError && (
              <p style={{
                color: '#dc2626',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '14px',
              }}>{productFormError}</p>
            )}
            {productFormSuccess && (
              <p style={{
                color: '#16a34a',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '14px',
              }}>{productFormSuccess}</p>
            )}

            <button onClick={handleSaveProduct} disabled={savingProduct} style={{
              width: '100%',
              background: savingProduct ? '#666' : '#111',
              color: 'white',
              border: 'none',
              padding: '16px',
              borderRadius: '14px',
              fontSize: '17px',
              fontWeight: '600',
              cursor: savingProduct ? 'not-allowed' : 'pointer',
            }}>
              {savingProduct ? 'Saving...' : (editingProductId ? 'Update Product' : 'Save Product')}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}